import { Box, TextField, Radio, FormControlLabel, InputAdornment, RadioGroup } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useMemo } from 'react';
import ProductCategoriesInput from './ProductCategoriesInput.jsx';
import { Palette } from '@/themes/themeOverrides/Palette.js';
import { criteriaConfig, resolveCriteriaKey } from '@/constants/CampaignCriteria.js';
import SingleProductSelector from './SingleProductSelector.jsx';

const CriteriaInput = ({ option, value, onChange, size = 'small', label, productSource, categorySource }) => {
    const translate = useTranslate();
    const key = useMemo(() => (criteriaConfig[option] ? option : resolveCriteriaKey(option)), [option]);
    const cfg = useMemo(() => (key ? criteriaConfig[key] : null), [key]);

    const renderInput = () => {
        if (!cfg) {
            return <TextField fullWidth label={label || option} value={value || ''} onChange={e => onChange(e.target.value)} size={size} />;
        }

        if (cfg.type === 'singleProduct') {
            return <SingleProductSelector value={value} onChange={onChange} label={label || cfg.label} hideImage source={productSource} />;
        }

        if (cfg.type === 'categories') {
            return (
                <ProductCategoriesInput
                    value={value}
                    onChange={onChange}
                    label={label || cfg.label}
                    source={categorySource}
                    sx={{
                        '& .actions': {
                            flexWrap: 'wrap',
                            '& .MuiButton-root': {
                                flexGrow: 1,
                            },
                        },
                    }}
                />
            );
        }

        if (cfg.type === 'radio') {
            return (
                <RadioGroup row value={value ?? cfg.initialValue} onChange={(_, v) => onChange(v)} aria-label={label || cfg.label}>
                    {cfg.options.map(opt => (
                        <FormControlLabel
                            key={opt.value}
                            value={opt.value}
                            control={<Radio size='small' color='primary' />}
                            label={translate(opt.label)}
                            sx={{
                                '& .MuiButtonBase-root': {
                                    pointerEvents: 'none',
                                    '&::before': {
                                        display: 'none',
                                    },
                                },
                            }}
                        />
                    ))}
                </RadioGroup>
            );
        }

        const endAdornment = cfg.adornment ? (
            <InputAdornment
                position='end'
                sx={{
                    '& .MuiTypography-root': {
                        fontSize: '10px',
                        fontWeight: 700,
                        color: Palette.gray.gray500,
                    },
                }}
            >
                {cfg.adornment}
            </InputAdornment>
        ) : undefined;

        return (
            <TextField
                fullWidth
                label={label || cfg.label}
                value={value ?? ''}
                onChange={e => onChange(e.target.value)}
                size={size}
                type={cfg.type === 'number' ? 'number' : 'text'}
                inputProps={cfg.inputProps}
                InputProps={endAdornment ? { endAdornment } : undefined}
                placeholder={cfg.placeholder}
            />
        );
    };

    return <Box sx={{ minWidth: '248px', maxWidth: '248px' }}>{renderInput()}</Box>;
};

export default CriteriaInput;
